import { SystemOptionType } from 'data/types';
import { abbreviateNumber } from 'utils/helpers';
import { isPtacCustomOption } from 'utils/ptac-custom-selections';
import {
  PtacSummaryRange,
  PtacSystemsSummary
} from 'utils/ptac-systems-summary-types';

export const PTAC_SYSTEMS_NOT_READY_TOOLTIP =
  'Place every unit to see matching systems.';

function rangeOf(values: number[]): PtacSummaryRange | null {
  const usable = values.filter((value) => Number.isFinite(value) && value > 0);
  if (usable.length === 0) return null;
  return { min: Math.min(...usable), max: Math.max(...usable) };
}

export function ptacCatalogOptions(
  options: SystemOptionType[]
): SystemOptionType[] {
  return options.filter((option) => !isPtacCustomOption(option));
}

export function summarizePtacSystems(
  options: SystemOptionType[]
): PtacSystemsSummary {
  const catalog = ptacCatalogOptions(options);
  return {
    count: catalog.length,
    priceRange: rangeOf(catalog.map((option) => option.price)),
    efficiencyRange: rangeOf(catalog.map((option) => option.averageSeer)),
    capacityRange: rangeOf(catalog.map((option) => option.btu))
  };
}

export function hasPtacSummaryRanges(summary: PtacSystemsSummary): boolean {
  return (
    summary.priceRange !== null ||
    summary.efficiencyRange !== null ||
    summary.capacityRange !== null
  );
}

function formatRange(
  range: PtacSummaryRange,
  format: (value: number) => string
): string {
  return range.min === range.max
    ? format(range.min)
    : `${format(range.min)} - ${format(range.max)}`;
}

export function formatPtacPriceRange(range: PtacSummaryRange): string {
  return formatRange(range, (value) => `$${abbreviateNumber(value)}`);
}

export function formatPtacEfficiencyRange(range: PtacSummaryRange): string {
  return `${formatRange(range, (value) => `${Math.round(value)}`)} EER`;
}

export function formatPtacCapacityRange(range: PtacSummaryRange): string {
  return `${formatRange(range, abbreviateNumber)} BTU`;
}
